import { h } from 'preact';
import styles from './ProductBadge.module.scss';
const badgeConfig = {
    // Product Status Labels (Priority 1 - Top)
    'Best Seller': { badge: 'Best Seller', position: 'Top', color: 'Yellow' },
    'Hot Deal': { badge: 'Hot Deal', position: 'Top', color: 'Red' },
    'Trending': { badge: 'Trending', position: 'Top', color: 'Navy' },
    'New': { badge: 'New', position: 'Top', color: 'Green' },
    // Promotion Labels (Priority 2 - Top)
    'Sale': { badge: 'Sale', position: 'Top', color: 'Red' },
    'Wait & Save': { badge: 'Wait & Save', position: 'Top', color: 'LightBlue' },
    'Clearance': { badge: 'Clearance', position: 'Top', color: 'Brown' },
    'Early Bird': { badge: 'Early Bird', position: 'Top', color: 'Gold' },
    'Package Deal': { badge: 'Package Deal', position: 'Top', color: 'DarkGreen' },
    'Flash Sales': { badge: 'Flash Sales', position: 'Top', color: 'Black' },
    // Free Shipping Labels (Priority 1 - Bottom)
    '-FreeShipping-': {
        badge: 'Free Shipping',
        position: 'Bottom',
        color: 'LightBrown',
    },
    'Free Shipping': {
        badge: 'Free Shipping',
        position: 'Bottom',
        color: 'LightBrown',
    },
    // Product Size Labels (Priority 2 - Bottom)
    'NZ SIZE': {
        badge: 'NZ SIZE',
        position: 'Bottom',
        color: 'Light',
    },
    'GLOBAL SIZE': {
        badge: 'GLOBAL SIZE',
        position: 'Bottom',
        color: 'Light',
    },
    'NZ SIZE/GLOBAL SIZE': {
        badge: 'NZ SIZE/GLOBAL SIZE',
        position: 'Bottom',
        color: 'Light',
    },
    // Shipping Method Labels (Priority 3 - Bottom)
    '-Pickup&Delivery-': {
        badge: 'Pickup & Delivery',
        position: 'Bottom',
        color: 'Light',
    },
    '-MustPickup-': {
        badge: 'Pickup Only',
        position: 'Bottom',
        color: 'Light',
    },
    '-MustDelivery-': {
        badge: 'Delivery Only',
        position: 'Bottom',
        color: 'Light',
    },
    'Must Delivery / Must Pickup': {
        badge: 'Must Delivery / Must Pickup',
        position: 'Bottom',
        color: 'Light',
    },
};
export function mapApiBadgesToProductBadges(apiBadges) {
    return apiBadges
        .filter(({ label }) => label in badgeConfig)
        .map(({ label }) => {
        const config = badgeConfig[label];
        return Object.assign(Object.assign({}, config), { key: label.toLowerCase().replace(/[^a-z0-9]+/g, '_') });
    });
}
// Function to extract and organize badges from product metafields
export function extractBadgesFromProduct(product) {
    if (!product.metafields || product.metafields.length === 0) {
        return { topBadges: [], bottomBadges: [] };
    }
    const allTopBadges = [];
    const bottomBadgesToShow = [];
    // Helper function to get metafield value by namespace and key
    const getMetafieldValue = (namespace, key) => {
        var _a;
        const metafield = (_a = product.metafields) === null || _a === void 0 ? void 0 : _a.find((field) => field.namespace === namespace && field.key === key);
        if (!metafield)
            return [];
        try {
            // Try to parse as JSON array first
            const parsed = JSON.parse(metafield.value);
            return Array.isArray(parsed) ? parsed : [metafield.value];
        }
        catch (_b) {
            // If not JSON, treat as single string
            return [metafield.value];
        }
    };
    // Product Status Labels (highest priority top)
    // namespace = "lab", key = "product_status_labels" 
    // These can be any of the following arrays
    // ['Best Seller'] Priority 1
    // ['Hot Deal'] Priority 1
    // ['Trending'] Priority 1
    // ['New'] Priority 1
    const productStatusLabels = getMetafieldValue('lab', 'product_status_labels');
    if (productStatusLabels.length > 0) {
        allTopBadges.push(...productStatusLabels);
    }
    // Promotion Labels (second priority top)
    // namespace = "lab", key = "promotion_labels"
    // These can be any of the following arrays
    // ['Sale']  Priority 2
    // ['Wait & Save'] Priority 2
    // ['Clearance'] Priority 2
    // ['Early Bird'] Priority 2
    // ['Package Deal'] Priority 2
    const promotionLabels = getMetafieldValue('lab', 'promotion_labels');
    if (promotionLabels.length > 0 && product.price.sale !== undefined) {
        // Only show promotion labels when there's a compare-at price
        // Sale takes precedence over other promotion labels
        if (promotionLabels.includes('Sale')) {
            allTopBadges.push('Sale');
        }
        else {
            // Show the first available promotion label if Sale is not present
            allTopBadges.push(promotionLabels[0]);
        }
    }
    // Bottom badges priority system: Free Shipping > Size > Delivery
    // Only show the highest priority available badge category
    // Free Shipping Labels (bottom priority 1 - highest)
    // namespace = "bac", key = "free_shipping"
    // ['-FreeShipping-'] Priority 1
    const freeShippingLabels = getMetafieldValue('bac', 'free_shipping');
    if (freeShippingLabels.length > 0) {
        bottomBadgesToShow.push(...freeShippingLabels);
    }
    // Product Size Labels (bottom priority 2 - medium)
    // namespace = "bac", key = "product_size_labels"
    // ['NZ SIZE'] Priority 2
    // ['GLOBAL SIZE'] Priority 2
    else {
        const productSizeLabels = getMetafieldValue('bac', 'product_size_labels');
        if (productSizeLabels.length > 0) {
            bottomBadgesToShow.push(...productSizeLabels);
        }
        // Shipping Method Labels (bottom priority 3 - lowest)
        // namespace = "bac", key = "shipping_method"
        // ['-Pickup&Delivery-'] Do NOT show a badge for this label
        // ['-MustPickup-'] Priority 1
        // ['-MustDelivery-'] Priority 2
        else {
            const shippingMethodLabels = getMetafieldValue('bac', 'shipping_method');
            // Filter out '-Pickup&Delivery-' so it does not show a badge
            const filteredShippingMethodLabels = shippingMethodLabels.filter(label => label !== '-Pickup&Delivery-');
            if (filteredShippingMethodLabels.length > 0) {
                bottomBadgesToShow.push(...filteredShippingMethodLabels);
            }
        }
    }
    // Map to badge objects
    const topApiBadges = allTopBadges.map(label => ({ label }));
    const bottomApiBadges = bottomBadgesToShow.map(label => ({ label }));
    const topMappedBadges = mapApiBadgesToProductBadges(topApiBadges);
    const bottomMappedBadges = mapApiBadgesToProductBadges(bottomApiBadges);
    // Filter by position (though top badges should only be Top and bottom badges should only be Bottom)
    const topBadges = topMappedBadges.filter(({ position }) => position === 'Top');
    const bottomBadges = bottomMappedBadges.filter(({ position }) => position === 'Bottom');
    return { topBadges, bottomBadges };
}
export function ProductBadge({ badge, position = 'Bottom', color = 'Light', }) {
    return (h("span", { className: `${styles.productBadges} ${styles[`productBadges${position}`]} ${styles[`productBadges${color}`]}` }, badge));
}
